import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Bold,
    CheckCircle2,
    Eye,
    ExternalLink,
    FileText,
    Heading2,
    Heading3,
    Highlighter,
    ImageIcon,
    Italic,
    Link2,
    List,
    Loader2,
    Palette,
    PencilLine,
    Quote,
    Save,
    Sparkles,
    Upload,
    Youtube,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { createPost, fetchAdminPost, updatePost, uploadImage } from "../../../services/api";
import { isFromDrive, resolveImageUrl } from "../../../utils/driveImage";
import { insertBlock, prefixLines, wrapSelection } from "../../../utils/textareaInsert";
import MarkdownContent from "../../../components/Blog/MarkdownContent";
import styles from "./PainelBlogForm.module.css";

// Paleta de cores para o botão de cor do editor (alinhada à marca).
const COLOR_PRESETS = [
    { label: "Azul", value: "#1d4ed8" },
    { label: "Verde", value: "#16a34a" },
    { label: "Vermelho", value: "#e11d48" },
    { label: "Laranja", value: "#ea580c" },
    { label: "Roxo", value: "#7c3aed" },
    { label: "Cinza", value: "#475569" },
];

const EMPTY_FORM = {
    title: "",
    slug: "",
    category: "",
    excerpt: "",
    meta_description: "",
    cover_image: "",
    author: "",
    content: "",
    status: "draft",
};

const META_DESCRIPTION_LIMIT = 155;
const EXCERPT_LIMIT = 220;

function toSlug(text = "") {
    return text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function countWords(text = "") {
    return text
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
}

function getReadingTime(text = "") {
    const words = countWords(text);
    if (!words) return "0 min";
    return `${Math.max(1, Math.ceil(words / 200))} min`;
}

function getSeoStatus(form) {
    const checks = [
        {
            label: "Título preenchido",
            valid: Boolean(form.title.trim()),
        },
        {
            label: "Slug definido",
            valid: Boolean(form.slug.trim()),
        },
        {
            label: "Resumo preenchido",
            valid: Boolean(form.excerpt.trim()),
        },
        {
            label: "Meta description entre 80 e 155 caracteres",
            valid:
                form.meta_description.trim().length >= 80 &&
                form.meta_description.trim().length <= META_DESCRIPTION_LIMIT,
        },
        {
            label: "Imagem de capa informada",
            valid: Boolean(form.cover_image.trim()),
        },
        {
            label: "Conteúdo com pelo menos 300 palavras",
            valid: countWords(form.content) >= 300,
        },
    ];

    const completed = checks.filter((item) => item.valid).length;

    return {
        checks,
        completed,
        total: checks.length,
        percentage: Math.round((completed / checks.length) * 100),
    };
}

function FieldHint({ children }) {
    return <p className={styles.fieldHint}>{children}</p>;
}

function CharacterCounter({ current, limit }) {
    const isOver = current > limit;

    return (
        <span className={`${styles.charCount} ${isOver ? styles.charOver : ""}`}>
            {current}/{limit}
        </span>
    );
}

export default function PainelBlogForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);

    const { password } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState(EMPTY_FORM);
    const [slugLocked, setSlugLocked] = useState(false);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSaving, setIsSaving] = useState(false);
    const [coverImageError, setCoverImageError] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [editorTab, setEditorTab] = useState("write");
    const [colorOpen, setColorOpen] = useState(false);
    const [isUploadingInline, setIsUploadingInline] = useState(false);
    const [isUploadingCover, setIsUploadingCover] = useState(false);

    const contentRef = useRef(null);
    const inlineFileRef = useRef(null);
    const coverFileRef = useRef(null);

    const seoStatus = useMemo(() => getSeoStatus(form), [form]);

    const wordCount = useMemo(() => countWords(form.content), [form.content]);
    const readingTime = useMemo(() => getReadingTime(form.content), [form.content]);

    // Resolve o link do Drive (ou qualquer URL) para URL direta de imagem
    const resolvedCoverUrl = resolveImageUrl(form.cover_image);
    const coverIsDrive = isFromDrive(form.cover_image);

    useEffect(() => {
        if (!isEdit) return;

        let isMounted = true;

        async function loadPost() {
            try {
                setIsLoading(true);
                setError("");

                const data = await fetchAdminPost(password, id);
                const p = data?.post || {};

                if (!isMounted) return;

                setForm({
                    title: p.title || "",
                    slug: p.slug || "",
                    category: p.category || "",
                    excerpt: p.excerpt || "",
                    meta_description: p.meta_description || "",
                    cover_image: p.cover_image || "",
                    author: p.author || "",
                    content: p.content || "",
                    status: p.status || "draft",
                });

                setSlugLocked(true);
                setCoverImageError(false);
            } catch (err) {
                if (!isMounted) return;

                setError(err?.message || "Não foi possível carregar o post.");
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadPost();

        return () => {
            isMounted = false;
        };
    }, [id, isEdit, password]);

    useEffect(() => {
        if (!form.cover_image) {
            setCoverImageError(false);
        }
    }, [form.cover_image]);

    const updateForm = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Aplica uma transformação ao conteúdo respeitando a seleção atual do textarea
    // e restaura o cursor após a atualização de estado.
    const applyToContent = (transform) => {
        const el = contentRef.current;
        const value = form.content;
        const start = el ? el.selectionStart : value.length;
        const end = el ? el.selectionEnd : value.length;
        const result = transform(value, start, end);

        updateForm("content", result.value);

        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(result.selectionStart, result.selectionEnd);
        });
    };

    const fmt = {
        bold: () => applyToContent((v, s, e) => wrapSelection(v, s, e, "**", "**", "texto em negrito")),
        italic: () => applyToContent((v, s, e) => wrapSelection(v, s, e, "*", "*", "texto em itálico")),
        h2: () => applyToContent((v, s, e) => prefixLines(v, s, e, "## ")),
        h3: () => applyToContent((v, s, e) => prefixLines(v, s, e, "### ")),
        list: () => applyToContent((v, s, e) => prefixLines(v, s, e, "- ")),
        quote: () => applyToContent((v, s, e) => prefixLines(v, s, e, "> ")),
        highlight: () => applyToContent((v, s, e) => wrapSelection(v, s, e, ":mark[", "]", "texto em destaque")),
        color: (value) => {
            setColorOpen(false);
            applyToContent((v, s, e) =>
                wrapSelection(v, s, e, ":color[", `]{value=${value}}`, "texto colorido")
            );
        },
        link: () => {
            const url = window.prompt("URL do link:", "https://");
            if (!url) return;
            applyToContent((v, s, e) => wrapSelection(v, s, e, "[", `](${url})`, "texto do link"));
        },
        youtube: () => {
            const url = window.prompt("Cole o link do vídeo do YouTube:", "https://www.youtube.com/watch?v=");
            if (!url) return;
            applyToContent((v, s, e) => insertBlock(v, s, e, url));
        },
        imageUrl: () => {
            const url = window.prompt("URL da imagem:", "https://");
            if (!url) return;
            const alt = window.prompt("Texto alternativo (opcional):", "") || "";
            applyToContent((v, s, e) => insertBlock(v, s, e, `![${alt}](${url})`));
        },
    };

    const handleInlineUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        try {
            setIsUploadingInline(true);
            setError("");
            const { url } = await uploadImage(password, file);
            applyToContent((v, s, e) => insertBlock(v, s, e, `![](${url})`));
        } catch (err) {
            setError(err?.message || "Falha no upload da imagem.");
        } finally {
            setIsUploadingInline(false);
        }
    };

    const handleCoverUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        try {
            setIsUploadingCover(true);
            setError("");
            const { url } = await uploadImage(password, file);
            updateForm("cover_image", url);
            setCoverImageError(false);
        } catch (err) {
            setError(err?.message || "Falha no upload da capa.");
        } finally {
            setIsUploadingCover(false);
        }
    };

    const handleTitleChange = (event) => {
        const title = event.target.value;

        setForm((prev) => ({
            ...prev,
            title,
            slug: slugLocked ? prev.slug : toSlug(title),
        }));
    };

    const handleSlugChange = (event) => {
        setSlugLocked(true);
        updateForm("slug", toSlug(event.target.value));
    };

    const handleGenerateSlug = () => {
        setSlugLocked(false);

        setForm((prev) => ({
            ...prev,
            slug: toSlug(prev.title),
        }));
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        updateForm(name, value);
    };

    const validateForm = () => {
        if (!form.title.trim()) {
            return "Informe o título do post.";
        }

        if (!form.slug.trim()) {
            return "Informe ou gere um slug para o post.";
        }

        if (!form.content.trim()) {
            return "Informe o conteúdo do post.";
        }

        if (form.meta_description.length > META_DESCRIPTION_LIMIT) {
            return `A meta description deve ter no máximo ${META_DESCRIPTION_LIMIT} caracteres.`;
        }

        if (form.excerpt.length > EXCERPT_LIMIT) {
            return `O resumo deve ter no máximo ${EXCERPT_LIMIT} caracteres.`;
        }

        return "";
    };

    const savePost = async (status) => {
        const validationError = validateForm();

        if (validationError) {
            setError(validationError);
            setSuccess("");
            return;
        }

        try {
            setIsSaving(true);
            setError("");
            setSuccess("");

            const payload = {
                ...form,
                title: form.title.trim(),
                slug: toSlug(form.slug || form.title),
                category: form.category.trim(),
                excerpt: form.excerpt.trim(),
                meta_description: form.meta_description.trim(),
                cover_image: form.cover_image.trim(),
                author: form.author.trim(),
                content: form.content.trim(),
                status,
            };

            if (isEdit) {
                await updatePost(password, id, payload);
            } else {
                await createPost(password, payload);
            }

            setSuccess(status === "published" ? "Post publicado com sucesso!" : "Rascunho salvo com sucesso!");

            setTimeout(() => {
                navigate("/painel/blog");
            }, 900);
        } catch (err) {
            setError(err?.message || "Não foi possível salvar o post.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        savePost(form.status);
    };

    if (isLoading) {
        return (
            <div className={styles.loadingPage}>
                <Loader2 size={28} className={styles.spinner} aria-hidden="true" />
                <span>Carregando post...</span>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <header className={styles.topbar}>
                <div>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={() => navigate("/painel/blog")}
                    >
                        <ArrowLeft size={17} aria-hidden="true" />
                        Voltar para o blog
                    </button>

                    <span className={styles.eyebrow}>Editor de conteúdo</span>

                    <h1>{isEdit ? "Editar post" : "Novo post"}</h1>

                    <p>
                        Escreva, revise o SEO e publique conteúdos para fortalecer
                        a presença digital da HL Software.
                    </p>
                </div>

                <div className={styles.topbarActions}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={() => savePost("draft")}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <Loader2 size={17} className={styles.spinner} aria-hidden="true" />
                        ) : (
                            <Save size={17} aria-hidden="true" />
                        )}
                        Salvar rascunho
                    </button>

                    <button
                        type="button"
                        className={styles.publishButton}
                        onClick={() => savePost("published")}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <Loader2 size={17} className={styles.spinner} aria-hidden="true" />
                        ) : (
                            <CheckCircle2 size={17} aria-hidden="true" />
                        )}
                        Publicar
                    </button>
                </div>
            </header>

            {error && (
                <div className={styles.errorMessage}>
                    <strong>Ops!</strong>
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className={styles.successMessage}>
                    <CheckCircle2 size={18} aria-hidden="true" />
                    <span>{success}</span>
                </div>
            )}

            <form className={styles.layout} onSubmit={handleSubmit}>
                <section className={styles.editorColumn}>
                    <div className={styles.formCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <h2>Informações principais</h2>
                                <p>Defina o título, slug e dados básicos do artigo.</p>
                            </div>
                        </div>

                        <div className={styles.formGrid}>
                            <div className={styles.fieldFull}>
                                <label htmlFor="title">Título *</label>
                                <input
                                    id="title"
                                    name="title"
                                    value={form.title}
                                    onChange={handleTitleChange}
                                    placeholder="Ex: Como a automação reduz custos em empresas"
                                    required
                                />
                            </div>

                            <div className={styles.fieldFull}>
                                <div className={styles.labelRow}>
                                    <label htmlFor="slug">Slug *</label>

                                    <div className={styles.slugActions}>
                                        {!slugLocked && (
                                            <span className={styles.autoTag}>automático</span>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleGenerateSlug}
                                        >
                                            Gerar pelo título
                                        </button>
                                    </div>
                                </div>

                                <input
                                    id="slug"
                                    name="slug"
                                    value={form.slug}
                                    onChange={handleSlugChange}
                                    placeholder="como-a-automacao-reduz-custos"
                                    required
                                />

                                <FieldHint>
                                    URL final: /blog/{form.slug || "slug-do-post"}
                                </FieldHint>
                            </div>

                            <div>
                                <label htmlFor="category">Categoria</label>
                                <input
                                    id="category"
                                    name="category"
                                    value={form.category}
                                    onChange={handleChange}
                                    placeholder="Ex: Automação, SaaS, Tecnologia"
                                />
                            </div>

                            <div>
                                <label htmlFor="author">Autor</label>
                                <input
                                    id="author"
                                    name="author"
                                    value={form.author}
                                    onChange={handleChange}
                                    placeholder="Ex: HL Software"
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.formCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <h2>Conteúdo do post</h2>
                                <p>Escreva o conteúdo principal do artigo.</p>
                            </div>

                            <div className={styles.contentStats}>
                                <span>{wordCount} palavras</span>
                                <span>{readingTime} leitura</span>
                            </div>
                        </div>

                        <div className={styles.fieldFull}>
                            <div className={styles.editorTabs}>
                                <button
                                    type="button"
                                    className={`${styles.editorTab} ${editorTab === "write" ? styles.editorTabActive : ""}`}
                                    onClick={() => setEditorTab("write")}
                                >
                                    <PencilLine size={15} aria-hidden="true" />
                                    Escrever
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.editorTab} ${editorTab === "preview" ? styles.editorTabActive : ""}`}
                                    onClick={() => setEditorTab("preview")}
                                >
                                    <Eye size={15} aria-hidden="true" />
                                    Pré-visualizar
                                </button>
                            </div>

                            {editorTab === "write" ? (
                                <>
                                    <div className={styles.toolbar}>
                                        <button type="button" title="Negrito" onClick={fmt.bold}>
                                            <Bold size={16} aria-hidden="true" />
                                        </button>
                                        <button type="button" title="Itálico" onClick={fmt.italic}>
                                            <Italic size={16} aria-hidden="true" />
                                        </button>
                                        <span className={styles.toolbarSep} aria-hidden="true" />
                                        <button type="button" title="Título (H2)" onClick={fmt.h2}>
                                            <Heading2 size={16} aria-hidden="true" />
                                        </button>
                                        <button type="button" title="Subtítulo (H3)" onClick={fmt.h3}>
                                            <Heading3 size={16} aria-hidden="true" />
                                        </button>
                                        <button type="button" title="Lista" onClick={fmt.list}>
                                            <List size={16} aria-hidden="true" />
                                        </button>
                                        <button type="button" title="Citação" onClick={fmt.quote}>
                                            <Quote size={16} aria-hidden="true" />
                                        </button>
                                        <span className={styles.toolbarSep} aria-hidden="true" />
                                        <button type="button" title="Link" onClick={fmt.link}>
                                            <Link2 size={16} aria-hidden="true" />
                                        </button>
                                        <button type="button" title="Destaque" onClick={fmt.highlight}>
                                            <Highlighter size={16} aria-hidden="true" />
                                        </button>
                                        <div className={styles.colorWrap}>
                                            <button
                                                type="button"
                                                title="Cor do texto"
                                                onClick={() => setColorOpen((o) => !o)}
                                            >
                                                <Palette size={16} aria-hidden="true" />
                                            </button>
                                            {colorOpen && (
                                                <div className={styles.colorPopover}>
                                                    {COLOR_PRESETS.map((c) => (
                                                        <button
                                                            key={c.value}
                                                            type="button"
                                                            title={c.label}
                                                            className={styles.colorSwatch}
                                                            style={{ background: c.value }}
                                                            onClick={() => fmt.color(c.value)}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <span className={styles.toolbarSep} aria-hidden="true" />
                                        <button type="button" title="Imagem por URL" onClick={fmt.imageUrl}>
                                            <ImageIcon size={16} aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            title="Enviar imagem"
                                            onClick={() => inlineFileRef.current?.click()}
                                            disabled={isUploadingInline}
                                        >
                                            {isUploadingInline ? (
                                                <Loader2 size={16} className={styles.spinner} aria-hidden="true" />
                                            ) : (
                                                <Upload size={16} aria-hidden="true" />
                                            )}
                                        </button>
                                        <button type="button" title="Vídeo do YouTube" onClick={fmt.youtube}>
                                            <Youtube size={16} aria-hidden="true" />
                                        </button>
                                        <input
                                            ref={inlineFileRef}
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleInlineUpload}
                                        />
                                    </div>

                                    <textarea
                                        id="content"
                                        name="content"
                                        ref={contentRef}
                                        value={form.content}
                                        onChange={handleChange}
                                        placeholder="Escreva o conteúdo do post aqui. Separe os parágrafos com uma linha em branco."
                                        rows={22}
                                        className={styles.contentArea}
                                        required
                                    />
                                    <p className={styles.contentHint}>
                                        💡 Use a barra acima para formatar. <strong>Negrito</strong>{" "}
                                        <code>**texto**</code>, títulos <code>## H2</code> /{" "}
                                        <code>### H3</code>, cor <code>:color[texto]{"{value=#1d4ed8}"}</code>,
                                        destaque <code>:mark[texto]</code>, imagem{" "}
                                        <code>![alt](url)</code> e vídeo: cole o link do YouTube em uma
                                        linha sozinha.
                                    </p>
                                </>
                            ) : (
                                <div className={styles.previewPane}>
                                    {form.content.trim() ? (
                                        <MarkdownContent content={form.content} />
                                    ) : (
                                        <p className={styles.previewEmpty}>
                                            Nada para pré-visualizar ainda. Escreva o conteúdo na aba
                                            "Escrever".
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.formCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <h2>SEO e listagem</h2>
                                <p>
                                    Ajuste como o post aparece no Google e na página
                                    pública do blog.
                                </p>
                            </div>
                        </div>

                        <div className={styles.formGrid}>
                            <div className={styles.fieldFull}>
                                <div className={styles.labelRow}>
                                    <label htmlFor="excerpt">Resumo</label>

                                    <CharacterCounter
                                        current={form.excerpt.length}
                                        limit={EXCERPT_LIMIT}
                                    />
                                </div>

                                <textarea
                                    id="excerpt"
                                    name="excerpt"
                                    value={form.excerpt}
                                    onChange={handleChange}
                                    placeholder="Breve descrição exibida na listagem do blog."
                                    rows={3}
                                    maxLength={EXCERPT_LIMIT + 40}
                                />

                                <FieldHint>
                                    Use um resumo curto e chamativo para aumentar o clique
                                    na listagem do blog.
                                </FieldHint>
                            </div>

                            <div className={styles.fieldFull}>
                                <div className={styles.labelRow}>
                                    <label htmlFor="meta_description">
                                        Meta description
                                    </label>

                                    <CharacterCounter
                                        current={form.meta_description.length}
                                        limit={META_DESCRIPTION_LIMIT}
                                    />
                                </div>

                                <textarea
                                    id="meta_description"
                                    name="meta_description"
                                    value={form.meta_description}
                                    onChange={handleChange}
                                    placeholder="Descrição para motores de busca, idealmente entre 80 e 155 caracteres."
                                    rows={3}
                                    maxLength={META_DESCRIPTION_LIMIT + 40}
                                />

                                <FieldHint>
                                    Esse texto pode aparecer nos resultados de busca.
                                    Seja direto, claro e inclua a principal palavra-chave.
                                </FieldHint>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formFooter}>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => savePost("draft")}
                            disabled={isSaving}
                        >
                            <Save size={17} aria-hidden="true" />
                            Salvar rascunho
                        </button>

                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2
                                        size={17}
                                        className={styles.spinner}
                                        aria-hidden="true"
                                    />
                                    Salvando...
                                </>
                            ) : isEdit ? (
                                "Salvar alterações"
                            ) : (
                                "Criar post"
                            )}
                        </button>
                    </div>
                </section>

                <aside className={styles.sideColumn}>
                    <div className={styles.sideCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <h2>Publicação</h2>
                                <p>Defina o status atual do conteúdo.</p>
                            </div>
                        </div>

                        <label htmlFor="status">Status</label>
                        <select
                            id="status"
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                        >
                            <option value="draft">Rascunho</option>
                            <option value="published">Publicado</option>
                        </select>

                        <div className={styles.statusHelp}>
                            {form.status === "published"
                                ? "Ao salvar, este post poderá aparecer publicamente no blog."
                                : "Rascunhos ficam disponíveis apenas no painel administrativo."}
                        </div>
                    </div>

                    <div className={styles.sideCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <h2>Imagem de capa</h2>
                                <p>Use uma imagem horizontal e leve.</p>
                            </div>
                        </div>

                        <label htmlFor="cover_image">URL da imagem</label>
                        <input
                            id="cover_image"
                            name="cover_image"
                            type="url"
                            value={form.cover_image}
                            onChange={handleChange}
                            placeholder="https://... ou link de compartilhamento do Drive"
                        />

                        <button
                            type="button"
                            className={styles.uploadCoverButton}
                            onClick={() => coverFileRef.current?.click()}
                            disabled={isUploadingCover}
                        >
                            {isUploadingCover ? (
                                <Loader2 size={15} className={styles.spinner} aria-hidden="true" />
                            ) : (
                                <Upload size={15} aria-hidden="true" />
                            )}
                            Enviar imagem do computador
                        </button>
                        <input
                            ref={coverFileRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleCoverUpload}
                        />

                        {coverIsDrive && (
                            <p className={styles.driveHint}>
                                <span className={styles.driveBadge}>Google Drive</span>
                                Link do Drive detectado e convertido automaticamente.
                                Certifique-se de que o arquivo está compartilhado como{" "}
                                <strong>Qualquer pessoa com o link</strong>.
                            </p>
                        )}

                        <div className={styles.coverBox}>
                            {resolvedCoverUrl && !coverImageError ? (
                                <img
                                    src={resolvedCoverUrl}
                                    alt="Preview da capa"
                                    className={styles.coverPreview}
                                    onError={() => setCoverImageError(true)}
                                />
                            ) : (
                                <div className={styles.coverFallback}>
                                    <ImageIcon size={26} aria-hidden="true" />
                                    <span>
                                        {form.cover_image
                                            ? "Não foi possível carregar a imagem"
                                            : "Preview da imagem de capa"}
                                    </span>
                                </div>
                            )}
                        </div>

                        {resolvedCoverUrl && (
                            <a
                                href={resolvedCoverUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.previewLink}
                            >
                                Abrir imagem
                                <ExternalLink size={14} aria-hidden="true" />
                            </a>
                        )}
                    </div>

                    <div className={styles.sideCard}>
                        <div className={styles.seoScoreHeader}>
                            <div>
                                <h2>Checklist SEO</h2>
                                <p>Itens básicos para publicar melhor.</p>
                            </div>

                            <strong>{seoStatus.percentage}%</strong>
                        </div>

                        <div className={styles.progressBar}>
                            <span style={{ width: `${seoStatus.percentage}%` }} />
                        </div>

                        <ul className={styles.seoChecklist}>
                            {seoStatus.checks.map((item) => (
                                <li
                                    key={item.label}
                                    className={item.valid ? styles.seoValid : ""}
                                >
                                    <CheckCircle2 size={16} aria-hidden="true" />
                                    <span>{item.label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={styles.sideCard}>
                        <div className={styles.previewHeader}>
                            <Sparkles size={18} aria-hidden="true" />
                            <h2>Preview do card</h2>
                        </div>

                        <div className={styles.postPreview}>
                            <div className={styles.previewImage}>
                                {resolvedCoverUrl && !coverImageError ? (
                                    <img src={resolvedCoverUrl} alt="" />
                                ) : (
                                    <FileText size={24} aria-hidden="true" />
                                )}
                            </div>

                            <div className={styles.previewBody}>
                                {form.category && (
                                    <span className={styles.previewCategory}>
                                        {form.category}
                                    </span>
                                )}

                                <h3>
                                    {form.title || "Título do post aparecerá aqui"}
                                </h3>

                                <p>
                                    {form.excerpt ||
                                        "O resumo do post aparecerá aqui na listagem pública do blog."}
                                </p>
                            </div>
                        </div>
                    </div>
                </aside>
            </form>
        </div>
    );
}